import { X, Crown, Zap, MessageSquare, Trophy, Sparkles, Camera, Utensils, Check, Mail, AlertCircle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { STRIPE_PRICES, PricingPlan, EARLY_BIRD_LIMIT } from "@/config/stripe";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isIOSNative } from "@/utils/platform";
import { usePlatformPurchase } from "@/hooks/usePlatformPurchase";
interface PaywallModalProps {
  userName: string;
  motivation?: string;
  isTrialExpired?: boolean;
  onUpgrade: (plan: PricingPlan) => void;
  onClose: () => void;
  loading?: boolean;
  earlyBirdSpotsLeft?: number;
  isEmailVerified?: boolean;
  userEmail?: string;
}

export function PaywallModal({ 
  userName, 
  motivation, 
  isTrialExpired = false, 
  onUpgrade, 
  onClose, 
  loading = false,
  earlyBirdSpotsLeft = 53,
  isEmailVerified = true,
  userEmail,
}: PaywallModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan>('yearly');
  const [resendingEmail, setResendingEmail] = useState(false);
  const motivationText = motivation || "your best self";
  
  const isEarlyBirdAvailable = earlyBirdSpotsLeft > 0;
  const isIOS = isIOSNative();
  const { restorePurchases, loading: restoreLoading, cancelPurchase } = usePlatformPurchase();
  
  // Calculate prices based on early bird availability
  const monthlyPrice = isEarlyBirdAvailable ? 11.99 : 19.99;
  const monthlyRegularPrice = 19.99;
  const yearlyPrice = isEarlyBirdAvailable ? 119.99 : 199;
  const yearlyRegularPrice = 199;
  const yearlyMonthly = (yearlyPrice / 12).toFixed(2);
  
  // Calculate savings percentage based on struck-through price
  const yearlySavingsPercent = isEarlyBirdAvailable 
    ? Math.round(((yearlyRegularPrice - yearlyPrice) / yearlyRegularPrice) * 100)
    : Math.round(((monthlyRegularPrice * 12 - yearlyPrice) / (monthlyRegularPrice * 12)) * 100);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-background/95 backdrop-blur-sm animate-fade-in">
      <div className="min-h-screen flex items-start justify-center p-4 pt-safe">
        <div className="relative w-full max-w-md max-h-[85vh] overflow-y-auto glass-card rounded-2xl p-4 pb-14 mt-12 space-y-3 border border-primary/30">
          {/* Close button - positioned outside safe area */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors z-10 bg-background/80 rounded-full p-1"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="text-center">
            <div className="w-12 h-12 rounded-full evolve-gradient flex items-center justify-center mx-auto mb-2 pulse-glow">
              <Crown className="w-6 h-6 text-foreground" />
            </div>
            
            {isTrialExpired ? (
              <>
                <h2 className="font-display text-lg font-bold mb-1">
                  Your 3-day trial is over, <span className="evolve-text-gradient">Evolver {userName}</span>
                </h2>
                <p className="text-xs text-muted-foreground">
                  This isn't a one-off plan — it's your <span className="text-foreground font-medium">personal AI coach for life</span>.
                </p>
              </>
            ) : (
              <>
                <h2 className="font-display text-lg font-bold mb-1">
                  Hey Evolver <span className="evolve-text-gradient">{userName}</span>
                </h2>
                <p className="text-xs text-muted-foreground">
                  You've reached your daily message limit.
                </p>
              </>
            )}
          </div>

          {/* Personal message */}
          <div className="text-center text-xs text-muted-foreground">
            <p>
              For less than a coffee a week, keep <span className="text-foreground font-medium">unlimited messages</span> and evolve into Legend.
            </p>
          </div>

          {/* What you get */}
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Premium includes:</p>
            <div className="grid grid-cols-2 gap-1.5 text-sm">
              <div className="flex items-center gap-2 bg-secondary/30 rounded-lg px-2.5 py-1.5">
                <MessageSquare className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-xs">Unlimited messages</span>
              </div>
              <div className="flex items-center gap-2 bg-secondary/30 rounded-lg px-2.5 py-1.5">
                <Utensils className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-xs">Daily food logging</span>
              </div>
              <div className="flex items-center gap-2 bg-secondary/30 rounded-lg px-2.5 py-1.5">
                <Camera className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-xs">Form checks</span>
              </div>
              <div className="flex items-center gap-2 bg-secondary/30 rounded-lg px-2.5 py-1.5">
                <Trophy className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-xs">Full tier access</span>
              </div>
              <div className="flex items-center gap-2 bg-secondary/30 rounded-lg px-2.5 py-1.5">
                <Zap className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-xs">Weekly nutrition</span>
              </div>
              <div className="flex items-center gap-2 bg-secondary/30 rounded-lg px-2.5 py-1.5">
                <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-xs">Progress graphics</span>
              </div>
            </div>
          </div>

          {/* Email Verification Warning */}
          {!isEmailVerified && (
            <div className="bg-yellow-500/20 border border-yellow-500/40 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-yellow-400" />
                <p className="text-xs font-medium text-yellow-400">Email verification required</p>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Please verify your email before upgrading to premium.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs"
                disabled={resendingEmail}
                onClick={async () => {
                  if (!userEmail) return;
                  setResendingEmail(true);
                  try {
                    const { error } = await supabase.auth.resend({
                      type: 'signup',
                      email: userEmail,
                    });
                    if (error) throw error;
                    toast.success('Verification email sent! Check your inbox.');
                  } catch (err) {
                    toast.error('Failed to send verification email');
                  } finally {
                    setResendingEmail(false);
                  }
                }}
              >
                <Mail className="w-3 h-3 mr-1" />
                {resendingEmail ? 'Sending...' : 'Resend verification email'}
              </Button>
            </div>
          )}

          {/* Early Bird Banner */}
          {isEmailVerified && isEarlyBirdAvailable ? (
            <div className="bg-primary/20 border border-primary/40 rounded-lg px-3 py-1.5 text-center">
              <p className="text-xs font-medium text-primary">
                🔥 <span className="font-bold">{earlyBirdSpotsLeft}</span> of 500 early bird spots left — <span className="font-bold">lifetime 40% off</span>
              </p>
            </div>
          ) : isEmailVerified ? (
            <div className="bg-secondary/30 border border-border/40 rounded-lg px-3 py-1.5 text-center">
              <p className="text-xs text-muted-foreground">
                Early bird pricing sold out — still a great deal!
              </p>
            </div>
          ) : null}

          {/* Plan Selection */}
          <div className="space-y-2">
            {/* Yearly Plan */}
            <button
              onClick={() => setSelectedPlan('yearly')}
              className={`w-full p-3 rounded-xl border-2 transition-all text-left ${
                selectedPlan === 'yearly'
                  ? 'border-primary bg-primary/10'
                  : 'border-border/50 bg-secondary/20 hover:border-border'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold">Yearly</span>
                    <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-medium">
                      Save {yearlySavingsPercent}%
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-display font-bold text-primary">
                      ${yearlyPrice.toFixed(2)}<span className="text-xs font-normal text-muted-foreground">/year</span>
                    </p>
                    {isEarlyBirdAvailable && (
                      <span className="text-xs text-muted-foreground line-through">$199</span>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Just ${yearlyMonthly}/month • Best value
                  </p>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedPlan === 'yearly' ? 'border-primary bg-primary' : 'border-border'
                }`}>
                  {selectedPlan === 'yearly' && <Check className="w-4 h-4 text-primary-foreground" />}
                </div>
              </div>
            </button>

            {/* Monthly Plan */}
            <button
              onClick={() => setSelectedPlan('monthly')}
              className={`w-full p-3 rounded-xl border-2 transition-all text-left ${
                selectedPlan === 'monthly'
                  ? 'border-primary bg-primary/10'
                  : 'border-border/50 bg-secondary/20 hover:border-border'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold">Monthly</span>
                    {isEarlyBirdAvailable && (
                      <span className="text-[10px] bg-secondary text-foreground px-1.5 py-0.5 rounded-full font-medium">
                        Save 40%
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-display font-bold text-primary">
                      ${monthlyPrice}<span className="text-xs font-normal text-muted-foreground">/month</span>
                    </p>
                    {isEarlyBirdAvailable && (
                      <span className="text-xs text-muted-foreground line-through">$19.99</span>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Cancel anytime
                  </p>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedPlan === 'monthly' ? 'border-primary bg-primary' : 'border-border'
                }`}>
                  {selectedPlan === 'monthly' && <Check className="w-4 h-4 text-primary-foreground" />}
                </div>
              </div>
            </button>
          </div>

          {/* CTA */}
          <div className="space-y-2 pb-4">
            <Button 
              variant="evolve" 
              className="w-full py-4 text-sm font-display"
              onClick={() => onUpgrade(selectedPlan)}
              disabled={loading || !isEmailVerified}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  <span>Processing...</span>
                </div>
              ) : !isEmailVerified ? (
                <>
                  <Mail className="w-5 h-5 mr-2" />
                  Verify Email First
                </>
              ) : (
                <>
                  <Crown className="w-5 h-5 mr-2" />
                  {isEarlyBirdAvailable ? 'Claim 40% Off — Go Premium' : 'Go Premium'}
                </>
              )}
            </Button>
            
            {/* Prominent Cancel button when loading */}
            {loading && (
              <Button 
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  cancelPurchase();
                  onClose();
                }}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel Purchase
              </Button>
            )}
            
            {/* iOS Restore Purchases Button */}
            {isIOS && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground"
                onClick={restorePurchases}
                disabled={restoreLoading}
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                {restoreLoading ? 'Restoring...' : 'Restore Purchases'}
              </Button>
            )}
            
            <p className="text-center text-xs text-muted-foreground">
              {loading ? 'Please complete checkout...' : !isEmailVerified ? 'Email verification required to continue' : 'Secure checkout • Cancel anytime'}
            </p>
            
            {/* Legal Links */}
            <div className="flex items-center justify-center gap-3 text-[10px] text-muted-foreground pt-1">
              <a 
                href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground hover:underline"
              >
                Terms of Use
              </a>
              <span>•</span>
              <a 
                href="https://evolveaicoach.app/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-foreground hover:underline"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
